import React, { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import AlertNotification from 'components/common/ui/AlertNotification/AlertNotification';
import { AlertMsg } from 'components/common/ui/AlertNotification/AlertNotification.types';
import {
  AppUIHeaderAlertParams,
  AppUIProviderProps,
} from './AppUIProvider.types';
import { AppUIContext } from './useAppUI';
import LoaderOverlay from 'src/components/Loader';
import { StatusBar, StatusBarInfo, Style } from '@capacitor/status-bar';
import { useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

export default function AppUIProvider({ children }: AppUIProviderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [openNotification, setOpenNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<AlertMsg>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [statusBar, setStatusBar] = useState<StatusBarInfo | undefined>();
  const { pathname } = useLocation();

  const [headerAlertProps, setHeaderAlertProps] =
    useState<AppUIHeaderAlertParams>();

  const getStatusBarInfo = () => {
    const platform = Capacitor.getPlatform();
    if (platform === 'web')
      return setStatusBar({ visible: false, style: Style.Light });

    StatusBar.getInfo().then((bar) => {
      setStatusBar(bar);
      StatusBar.setStyle({ style: Style.Light });
      return bar.visible
        ? StatusBar.setOverlaysWebView({ overlay: true })
        : undefined;
    });
  };

  useEffect(() => {
    getStatusBarInfo();
  }, []);

  useEffect(() => {
    !['/', '/categories'].includes(pathname) && window.scrollTo(0, 0);
  }, [pathname]);

  const handleCloseNotification = (
    _event: React.SyntheticEvent | Event,
    reason?: string | undefined
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenNotification(false);
  };

  const handleAlert = ({
    msg,
    type,
  }: {
    msg: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }) => {
    setNotificationMessage({ type, msg });
    setOpenNotification(true);
  };

  const contextValue = {
    alertNotification: {
      success: (text: string) => {
        handleAlert({ msg: text, type: 'success' });
      },
      error: (text: string) => {
        handleAlert({ msg: text, type: 'error' });
      },
      info: (text: string) => {
        handleAlert({ msg: text, type: 'info' });
      },
      warning: (text: string) => {
        handleAlert({ msg: text, type: 'warning' });
      },
    },
    headerAlert: {
      show: (params: AppUIHeaderAlertParams) => {
        setHeaderAlertProps(params);
      },
      hide: () => {
        setHeaderAlertProps(undefined);
      },
    },
    isMobile,
    isXs,
    setIsLoading,
    isLoading,
    statusBar,
    headerAlertProps,
  };

  return (
    <AppUIContext.Provider value={contextValue}>
      {children}
      <AlertNotification
        open={openNotification}
        onClose={handleCloseNotification}
        alert={notificationMessage}
      />
      {isLoading && <LoaderOverlay />}
    </AppUIContext.Provider>
  );
}
